import { LitElement, html, css } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import "@awesome.me/webawesome/dist/components/icon/icon.js";
import "@awesome.me/webawesome/dist/components/dropdown/dropdown.js";
import "@awesome.me/webawesome/dist/components/dropdown-item/dropdown-item.js";
import "@awesome.me/webawesome/dist/components/divider/divider.js";
import "@awesome.me/webawesome/dist/components/dialog/dialog.js";
import "@awesome.me/webawesome/dist/components/button/button.js";
import "@awesome.me/webawesome/dist/components/callout/callout.js";
import { authService, type UserRole } from "../services/auth";
import { syncService, type SyncState } from "../services/sync";
import { settingsSyncService } from "../services/settings-sync";
import { monitoredUserSelectionService } from "../services/monitored-user-selection";
import "./debug-dialog";
import "./app-drawer";
import { Router } from "@vaadin/router";

@customElement("app-shell")
export class AppShell extends LitElement {
  @property({ type: String }) currentPath = "/";
  @property({ type: String }) pageTitle = "DAFNE Log";

  /** Show a save button instead of sync in the header (for edit pages) */
  @property({ type: Boolean }) showSave = false;
  @property({ type: Boolean }) saveDisabled = false;
  @property({ type: Boolean }) saveLoading = false;

  @state() private roles: UserRole[] = [];
  @state() private isAdmin = false;
  @state() private displayName = "";
  @state() private syncStatus: SyncState["status"] = "idle";
  @state() private lastSyncTime?: Date;
  @state() private pendingChanges = 0;
  @state() private debugDialogOpen = false;
  @state() private settingsChangedDialogOpen = false;
  @state() private drawerOpen = false;
  @state() private isLargeScreen = false;

  private _syncUnsubscribe?: () => void;
  private _settingsSyncUnsubscribe?: () => void;
  private _authUnsubscribe?: () => void;
  private _mediaQuery?: MediaQueryList;
  private _mediaListener = (e: MediaQueryListEvent) => {
    this.isLargeScreen = e.matches;
  };

  static styles = css`
    *,
    *::before,
    *::after {
      box-sizing: border-box;
    }

    :host {
      display: block;
      min-height: 100vh;
      --mobile-header-height: 60px;
      --desktop-drawer-width: 240px;
    }

    .app-container {
      display: flex;
      min-height: 100vh;
      max-width: 100%;
    }

    .main-wrapper {
      flex: 1;
      display: flex;
      flex-direction: column;
      min-height: 100vh;
      width: 100vw;
      max-width: 100vw;
    }

    /* ---------- Header ---------- */
    header {
      background-color: var(--color-chrome, #e0edff);
      color: var(--color-text, #1f2d3d);
      padding: calc(env(safe-area-inset-top) + 0.5rem) 0.75rem 0.5rem;
      position: fixed;
      top: 0;
      left: 0;
      right: 0;
      z-index: 1100;
      max-width: 100%;
    }

    .header-grid {
      display: grid;
      grid-template-columns: auto 1fr auto;
      align-items: center;
      gap: 0.5rem;
      min-height: 2.5rem;
    }

    .icon-btn {
      display: flex;
      align-items: center;
      justify-content: center;
      cursor: pointer;
      padding: 0.5rem;
      border-radius: 0.5rem;
      transition: all 0.15s;
      background: rgba(31, 45, 61, 0.06);
      border: 1px solid rgba(31, 45, 61, 0.12);
      color: inherit;
    }

    .icon-btn:hover {
      background: rgba(31, 45, 61, 0.1);
    }

    .icon-btn:active {
      transform: scale(0.96);
    }

    .icon-btn wa-icon {
      font-size: 1.25rem;
    }

    .header-center {
      display: flex;
      align-items: center;
      justify-content: center;
      min-width: 0;
    }

    .header-title {
      margin: 0;
      font-size: 1.05rem;
      font-weight: 700;
      color: var(--color-text, #1f2d3d);
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    .header-right {
      display: flex;
      align-items: center;
      gap: 0.5rem;
    }

    .profile-trigger {
      display: flex;
      align-items: center;
      gap: 0.4rem;
      cursor: pointer;
      padding: 0.4rem 0.6rem;
      border-radius: 999px;
      background: rgba(31, 45, 61, 0.06);
      border: 1px solid rgba(31, 45, 61, 0.12);
      color: var(--color-text, #1f2d3d);
    }

    .profile-trigger:hover {
      background: rgba(31, 45, 61, 0.1);
    }

    .profile-trigger wa-icon {
      font-size: 1.4rem;
    }

    /* Save button (edit pages) */
    .header-save {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      cursor: pointer;
      padding: 0.5rem 0.75rem;
      border-radius: 0.5rem;
      font-size: 0.85rem;
      font-weight: 600;
      background: var(--color-accent, #bd64fc);
      border: 1px solid var(--color-accent, #bd64fc);
      color: white;
    }

    .header-save.disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }

    .header-save.loading wa-icon {
      animation: spin 1s linear infinite;
    }

    @keyframes spin {
      to {
        transform: rotate(360deg);
      }
    }

    main {
      flex: 1;
      padding-top: calc(var(--mobile-header-height) + env(safe-area-inset-top));
      padding-bottom: 6rem;
    }

    /* ---------- Bottom nav ---------- */
    .bottom-nav {
      position: fixed;
      bottom: 0;
      left: 0;
      right: 0;
      background: var(--color-chrome, #e0edff);
      border-top: 1px solid var(--color-border, #dce6f4);
      border-top-left-radius: 1rem;
      border-top-right-radius: 1rem;
      display: grid;
      grid-template-columns: 1fr 1fr 1fr auto;
      align-items: center;
      padding-bottom: env(safe-area-inset-bottom);
      z-index: 1050;
      height: 4.25rem;
    }

    .bottom-nav .nav-item {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      padding: 0.5rem 0.25rem;
      color: var(--color-text-soft, #6c757d);
      text-decoration: none;
      font-size: 0.7rem;
      transition: color 0.15s;
      cursor: pointer;
      background: none;
      border: none;
      height: 100%;
    }

    .bottom-nav .nav-item.active {
      color: var(--color-accent, #bd64fc);
    }

    .bottom-nav .nav-item wa-icon {
      font-size: 1.5rem;
      margin-bottom: 0.15rem;
    }

    .bottom-actions {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      padding: 0 0.75rem;
    }

    .fab {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 3rem;
      height: 3rem;
      border-radius: 999px;
      border: none;
      cursor: pointer;
      color: white;
      box-shadow: 0 2px 6px rgba(0, 0, 0, 0.2);
      transition: transform 0.1s;
    }

    .fab:active {
      transform: scale(0.94);
    }

    .fab wa-icon {
      font-size: 1.4rem;
    }

    .fab.gl {
      background: var(--color-accent-pink, #d63384);
    }

    .fab.entry {
      background: var(--color-accent, #bd64fc);
    }

    /* ---------- Desktop layout (>=1024) ---------- */
    @media (min-width: 1024px) {
      .main-wrapper {
        margin-left: var(--desktop-drawer-width);
        width: calc(100vw - var(--desktop-drawer-width));
        max-width: calc(100vw - var(--desktop-drawer-width));
      }

      header {
        left: var(--desktop-drawer-width);
      }

      .bottom-nav {
        left: var(--desktop-drawer-width);
      }
    }
  `;

  connectedCallback() {
    super.connectedCallback();
    this.currentPath = window.location.pathname;
    window.addEventListener("vaadin-router-location-changed", () => {
      this.currentPath = window.location.pathname;
      window.scrollTo(0, 0);
    });

    // Track large-screen breakpoint for persistent drawer
    this._mediaQuery = window.matchMedia("(min-width: 1024px)");
    this.isLargeScreen = this._mediaQuery.matches;
    this._mediaQuery.addEventListener("change", this._mediaListener);

    // Auth state
    const authState = authService.state;
    this.roles = authState.roles || [];
    this.isAdmin = authState.isAdmin || false;
    this.displayName = authState.displayName || "";

    this._authUnsubscribe = authService.subscribe((state) => {
      this.roles = state.roles || [];
      this.isAdmin = state.isAdmin || false;
      this.displayName = state.displayName || "";
      if (state.isAuthenticated) {
        settingsSyncService.start();
        if (this._isMonitorOnly && !monitoredUserSelectionService.loaded) {
          monitoredUserSelectionService.load();
        }
      } else {
        settingsSyncService.stop();
        monitoredUserSelectionService.reset();
      }
    });

    if (authState.isAuthenticated) {
      settingsSyncService.start();
      if (this._isMonitorOnly) {
        monitoredUserSelectionService.load();
      }
    }

    this._settingsSyncUnsubscribe = settingsSyncService.subscribe((event) => {
      if (event.dataChanged) {
        this.settingsChangedDialogOpen = true;
      }
    });

    this._syncUnsubscribe = syncService.subscribe((state) => {
      this.syncStatus = state.status;
      this.lastSyncTime = state.lastSyncTime;
      this.pendingChanges = state.pendingChanges;
    });

  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this._syncUnsubscribe?.();
    this._settingsSyncUnsubscribe?.();
    this._authUnsubscribe?.();
    this._mediaQuery?.removeEventListener("change", this._mediaListener);
    settingsSyncService.stop();
  }

  private _handleSettingsChangedReview() {
    this.settingsChangedDialogOpen = false;
    Router.go("/settings");
  }

  private _handleSettingsChangedDismiss() {
    this.settingsChangedDialogOpen = false;
  }

  private async _triggerSync() {
    if (this.syncStatus === "syncing" || this.syncStatus === "checking") return;
    await syncService.checkAndSync();
  }

  private _handleSave() {
    if (this.saveDisabled || this.saveLoading) return;
    this.dispatchEvent(
      new CustomEvent("save-request", { bubbles: true, composed: true }),
    );
  }

  private async _handleLogout() {
    if (confirm("Are you sure you want to log out?")) {
      await authService.logout();
      // Full reload so every per-user page @state and module-level
      // singleton (selectedDateService, etc.) starts fresh for the next
      // user. Avoids stale greetings and cross-user UI leaks (#24).
      window.location.href = "/";
    }
  }

  private get _isMonitorOnly(): boolean {
    return (
      (this.roles.includes("monitor") || this.roles.includes("clinician")) &&
      !this.roles.includes("diabetic")
    );
  }

  private _openDrawer = () => {
    this.drawerOpen = true;
  };

  private _closeDrawer = () => {
    this.drawerOpen = false;
  };

  private _handleDrawerNavigate = (e: CustomEvent<{ path: string }>) => {
    this.drawerOpen = false;
    Router.go(e.detail.path);
  };

  render() {
    return html`
      <div class="app-container">
        <app-drawer
          ?open=${this.drawerOpen}
          ?persistent=${this.isLargeScreen}
          .currentPath=${this.currentPath}
          .roles=${this.roles}
          .isAdmin=${this.isAdmin}
          .syncStatus=${this.syncStatus}
          .lastSyncTime=${this.lastSyncTime}
          .pendingChanges=${this.pendingChanges}
          @drawer-navigate=${this._handleDrawerNavigate}
          @drawer-close=${this._closeDrawer}
          @drawer-sync=${this._triggerSync}
          @drawer-report-issue=${() => {
            this.drawerOpen = false;
            this.debugDialogOpen = true;
          }}
        ></app-drawer>

        <div class="main-wrapper">
          <header>
            <div class="header-grid">
              ${this.isLargeScreen
                ? html`<span></span>`
                : html`
                    <button
                      class="icon-btn"
                      @click=${this._openDrawer}
                      title="Menu"
                      aria-label="Open menu"
                    >
                      <wa-icon name="bars"></wa-icon>
                    </button>
                  `}

              <div class="header-center">
                <slot name="header-center">
                  <h1 class="header-title">${this.pageTitle}</h1>
                </slot>
              </div>

              <div class="header-right">
                ${this.showSave
                  ? html`
                      <div
                        class="header-save ${this.saveDisabled
                          ? "disabled"
                          : ""} ${this.saveLoading ? "loading" : ""}"
                        @click=${this._handleSave}
                        title="Save"
                      >
                        <wa-icon
                          name=${this.saveLoading
                            ? "arrows-rotate"
                            : "floppy-disk"}
                        ></wa-icon>
                        <span>Save</span>
                      </div>
                    `
                  : html`
                      <wa-dropdown placement="bottom-end" hoist>
                        <button
                          slot="trigger"
                          class="profile-trigger"
                          aria-label="Profile menu"
                        >
                          <wa-icon name="circle-user"></wa-icon>
                        </button>
                        <wa-dropdown-item disabled>
                          ${this.displayName || "Signed in"}
                        </wa-dropdown-item>
                        <wa-divider></wa-divider>
                        ${!this._isMonitorOnly
                          ? html`<wa-dropdown-item
                              @click=${() => Router.go("/settings")}
                            >
                              <wa-icon slot="prefix" name="gear"></wa-icon>
                              Settings
                            </wa-dropdown-item>`
                          : ""}
                        <wa-dropdown-item @click=${this._handleLogout}>
                          <wa-icon
                            slot="prefix"
                            name="right-from-bracket"
                          ></wa-icon>
                          Logout
                        </wa-dropdown-item>
                      </wa-dropdown>
                    `}
              </div>
            </div>
          </header>

          <main>
            <slot></slot>
          </main>

          <nav class="bottom-nav"
            style="grid-template-columns: ${this._isMonitorOnly ? "1fr 1fr 1fr 1fr" : "1fr 1fr 1fr auto"}"
          >
            <a
              href="/dashboard"
              class="nav-item ${this.currentPath === "/dashboard"
                ? "active"
                : ""}"
            >
              <wa-icon name="gauge"></wa-icon>
              Dashboard
            </a>
            <a
              href="/"
              class="nav-item ${this.currentPath === "/" ? "active" : ""}"
            >
              <wa-icon name="calendar-day"></wa-icon>
              Today
            </a>
            <a
              href="/history"
              class="nav-item ${this.currentPath === "/history"
                ? "active"
                : ""}"
            >
              <wa-icon name="chart-bar"></wa-icon>
              History
            </a>
            ${this._isMonitorOnly
              ? html`
                  <a
                    href="/monitoring"
                    class="nav-item ${this.currentPath === "/monitoring"
                      ? "active"
                      : ""}"
                  >
                    <wa-icon name="users"></wa-icon>
                    People
                  </a>
                `
              : html`
                  <div class="bottom-actions">
                    <button
                      class="fab gl"
                      @click=${() => Router.go("/quick-gl")}
                      title="Add reading"
                      aria-label="Add glucose reading"
                    >
                      <wa-icon name="droplet"></wa-icon>
                    </button>
                    <button
                      class="fab entry"
                      @click=${() => Router.go("/add")}
                      title="Add entry"
                      aria-label="Add entry"
                    >
                      <wa-icon name="plus"></wa-icon>
                    </button>
                  </div>
                `}
          </nav>
        </div>
      </div>

      <debug-dialog
        ?open=${this.debugDialogOpen}
        @close=${() => (this.debugDialogOpen = false)}
      ></debug-dialog>

      <wa-dialog
        ?open=${this.settingsChangedDialogOpen}
        label="Settings Updated"
        @wa-request-close=${this._handleSettingsChangedDismiss}
      >
        <wa-callout variant="warning" open>
          <wa-icon slot="icon" name="triangle-exclamation"></wa-icon>
          Your settings were updated from another device. Please review them to
          ensure they are correct.
        </wa-callout>
        <div
          style="display: flex; gap: 0.5rem; justify-content: flex-end; margin-top: 1rem;"
        >
          <wa-button
            variant="neutral"
            @click=${this._handleSettingsChangedDismiss}
          >
            Dismiss
          </wa-button>
          <wa-button
            variant="brand"
            @click=${this._handleSettingsChangedReview}
          >
            <wa-icon slot="prefix" name="gear"></wa-icon>
            Review Settings
          </wa-button>
        </div>
      </wa-dialog>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "app-shell": AppShell;
  }
}
